import { GamePlayScene } from '../scenes/GamePlayScene';
import { Tower } from '../entities/towers/Tower';
import { Earth } from '../entities/earth/Earth';
import { LaserTower } from '../entities/towers/LaserTower';
import { RapidFireTower } from '../entities/towers/RapidFireTower';
import { HeavyCannon } from '../entities/towers/HeavyCannon';
import { MissileTower } from '../entities/towers/MissileTower';
import { FreezeTower } from '../entities/towers/FreezeTower';
import { MultiDirectionalTower } from '../entities/towers/MultiDirectionalTower';
import { PiercingTower } from '../entities/towers/PiercingTower';
import { ChainLightningTower } from '../entities/towers/ChainLightningTower';
import { ShotgunTower } from '../entities/towers/ShotgunTower';
import { BeamTower } from '../entities/towers/BeamTower';
import { AudioManager, SoundEffect } from '../systems/AudioManager';
import { t } from '../i18n/lang';

export interface TowerPlacementResult {
  success: boolean;
  cost: number;
  tower?: Tower;
  message?: string;
}

export class TowerPlacementManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  
  // Tower placement state
  private isPlacingTower: boolean = false;
  private selectedTowerType: string = '';
  private towerPlacementPreview: Phaser.GameObjects.Graphics | null = null;
  private selectedTower: Tower | null = null;
  private upgradePanel: Phaser.GameObjects.Container | null = null;
  private towerTooltip: Phaser.GameObjects.Container | null = null;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
  }

  startTowerPlacement(towerType: string, energyCredits: number): boolean {
    const cost = this.getTowerCost(towerType);
    if (energyCredits < cost) return false;
    
    this.isPlacingTower = true;
    this.selectedTowerType = towerType;
    this.towerPlacementPreview = this.scene.add.graphics();
    
    // Play tower placement sound
    this.audioManager.playSFX(SoundEffect.TOWER_PLACE);
    return true;
  }

  placeTower(x: number, y: number, earth: Earth, energyCredits: number, towers: Tower[]): TowerPlacementResult {
    if (!this.isPlacingTower) {
      return { success: false, cost: 0, message: 'Not in placement mode' };
    }
    
    // Validation logic
    const distanceToEarth = Phaser.Math.Distance.Between(x, y, earth.x, earth.y);
    // if (distanceToEarth <= 100) {
    //   return { success: false, cost: 0, message: 'Too close to Earth' };
    // }
    
    if (towers.length >= earth.getTowerLimit()) {
      return { success: false, cost: 0, message: 'Tower limit reached' };
    }
    
    const cost = this.getTowerCost(this.selectedTowerType);
    if (energyCredits < cost) {
      return { success: false, cost: 0, message: 'Not enough credits' };
    }
    
    // Create tower at clicked position first
    const tower = this.createTowerByType(this.selectedTowerType, x, y);
    if (!tower) {
      return { success: false, cost: 0, message: 'Failed to create tower' };
    }
    
    // Add tower to scene (not to Earth to avoid relative positioning)
    this.scene.add.existing(tower);
    
    console.log(`Tower placed at (${x}, ${y}), type: ${this.selectedTowerType}`);
    
    this.stopTowerPlacement();
    return { success: true, cost, tower };
  }

  updateTowerPlacementPreview(x: number, y: number): void {
    if (!this.towerPlacementPreview) return;
    
    this.towerPlacementPreview.clear();
    
    // Check if placement is valid (not too close to Earth)
    const earthPosX = this.scene.cameras.main.centerX;
    const earthPosY = this.scene.cameras.main.centerY;
    const distanceToEarth = Phaser.Math.Distance.Between(x, y, earthPosX, earthPosY);
    const isValidPlacement = distanceToEarth > 100; // Minimum distance from Earth
    
    const color = isValidPlacement ? 0x00ff00 : 0xff0000;
    const size = this.getTowerSize(this.selectedTowerType);
    this.towerPlacementPreview.lineStyle(2, color);
    this.towerPlacementPreview.strokeCircle(x, y, size);
  }

  stopTowerPlacement(): void {
    this.isPlacingTower = false;
    this.selectedTowerType = '';
    if (this.towerPlacementPreview) {
      this.towerPlacementPreview.destroy();
      this.towerPlacementPreview = null;
    }
  }

  updateTowerPositions(earth: Earth, towers: Tower[]): void {
    if (!earth || towers.length === 0) return;
    
    // Only auto-arrange if towers are too close to each other or Earth
    const needsArrangement = this.checkIfArrangementNeeded(earth, towers);
    
    if (!needsArrangement) {
      // Just validate current positions are not overlapping with Earth
      towers.forEach(tower => {
        const distanceToEarth = Phaser.Math.Distance.Between(tower.x, tower.y, earth.x, earth.y);
        if (distanceToEarth < 80) {
          // Move tower to safe distance if too close
          const angle = Math.atan2(tower.y - earth.y, tower.x - earth.x);
          tower.x = earth.x + Math.cos(angle) * 100;
          tower.y = earth.y + Math.sin(angle) * 100;
        }
      });
      return;
    }
    
    // Auto-arrange towers in circle around Earth
    const n = towers.length;
    const R = 100; // radius around Earth
    
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n;
      const tower = towers[i];
      if (!tower) continue;
      
      const newX = earth.x + Math.cos(angle) * R;
      const newY = earth.y + Math.sin(angle) * R;
      
      // Smooth transition to new position
      this.scene.tweens.add({
        targets: tower,
        x: newX,
        y: newY,
        duration: 500,
        ease: 'Power2'
      });
    }
  }

  private checkIfArrangementNeeded(earth: Earth, towers: Tower[]): boolean {
    // Check if any tower is too close to Earth
    for (const tower of towers) {
      const distanceToEarth = Phaser.Math.Distance.Between(tower.x, tower.y, earth.x, earth.y);
      if (distanceToEarth < 80) {
        return true;
      }
    }
    
    // Check if towers are too close to each other
    for (let i = 0; i < towers.length; i++) {
      for (let j = i + 1; j < towers.length; j++) {
        const distance = Phaser.Math.Distance.Between(
          towers[i]?.x || 0, towers[i]?.y || 0,
          towers[j]?.x || 0, towers[j]?.y || 0
        );
        if (distance < 50) { // Minimum distance between towers
          return true;
        }
      }
    }
    
    return false;
  }

  selectTower(x: number, y: number, towers: Tower[]): Tower | null {
    // Check if clicking on a tower
    for (const tower of towers) {
      // Use direct tower position instead of world transform
      const distance = Phaser.Math.Distance.Between(x, y, tower.x, tower.y);
      const towerSize = this.getTowerSizeByType(tower);
      
      if (distance <= towerSize + 10) { // Add small buffer for easier clicking
        this.selectedTower = tower;
        this.showTowerTooltip(tower, tower.x, tower.y);
        return tower;
      }
    }
    
    // Clicked elsewhere, hide panels
    this.hideUpgradePanel();
    this.hideTowerTooltip();
    return null;
  }

  upgradeTower(tower: Tower, energyCredits: number): { success: boolean; cost: number; newCredits: number } {
    const upgradeCost = tower.getLevel() * 50;
    if (energyCredits < upgradeCost) {
      return { success: false, cost: upgradeCost, newCredits: energyCredits };
    }
    
    // Upgrade tower
    tower.upgrade();
    
    // Play upgrade sound
    this.audioManager.playSFX(SoundEffect.TOWER_UPGRADE);
    
    const newCredits = energyCredits - upgradeCost;
    
    // Update upgrade panel if showing
    if (this.upgradePanel && this.selectedTower === tower) {
      this.showUpgradePanel(tower, newCredits);
    }
    
    // Update tooltip if showing
    if (this.towerTooltip && this.selectedTower === tower) {
      const world = tower.getWorldTransformMatrix();
      this.showTowerTooltip(tower, world.tx, world.ty);
    }
    
    return { success: true, cost: upgradeCost, newCredits };
  }

  sellTower(tower: Tower, earth: Earth, towers: Tower[]): void {
    // Remove from Earth and scene
    if (earth) earth.remove(tower);
    const idx = towers.indexOf(tower);
    if (idx >= 0) towers.splice(idx, 1);
    
    tower.destroy();
    this.selectedTower = null;
    this.hideTowerTooltip();
    this.hideUpgradePanel();
    
    this.updateTowerPositions(earth, towers);
  }

  private createTowerByType(towerType: string, x: number, y: number): Tower | null {
    switch (towerType) {
      case 'laser':
        return new LaserTower(this.scene, x, y);
      case 'rapid':
        return new RapidFireTower(this.scene, x, y);
      case 'heavy':
        return new HeavyCannon(this.scene, x, y);
      case 'missile':
        return new MissileTower(this.scene, x, y);
      case 'freeze':
        return new FreezeTower(this.scene, x, y);
      case 'multi':
        return new MultiDirectionalTower(this.scene, x, y);
      case 'piercing':
        return new PiercingTower(this.scene, x, y);
      case 'chain':
        return new ChainLightningTower(this.scene, x, y);
      case 'shotgun':
        return new ShotgunTower(this.scene, x, y);
      case 'beam':
        return new BeamTower(this.scene, x, y);
      default:
        return new LaserTower(this.scene, x, y);
    }
  }

  private getTowerCost(towerType: string): number {
    switch (towerType) {
      case 'laser': return 100;
      case 'rapid': return 150;
      case 'heavy': return 200;
      case 'missile': return 250;
      case 'freeze': return 180;
      case 'multi': return 180;
      case 'piercing': return 220;
      case 'chain': return 260;
      case 'shotgun': return 170;
      case 'beam': return 300;
      default: return 100;
    }
  }

  private getTowerSize(towerType: string): number {
    switch (towerType) {
      case 'laser': return 15;
      case 'rapid': return 12;
      case 'heavy': return 18;
      default: return 15;
    }
  }

  private getTowerSizeByType(tower: Tower): number {
    if (tower instanceof HeavyCannon) return 18;
    if (tower instanceof RapidFireTower) return 12;
    if (tower instanceof MultiDirectionalTower) return 15;
    if (tower instanceof PiercingTower) return 14;
    if (tower instanceof ChainLightningTower) return 15;
    if (tower instanceof ShotgunTower) return 13;
    if (tower instanceof BeamTower) return 15;
    return 15; // LaserTower default
  }

  private showTowerTooltip(tower: Tower, x: number, y: number): void {
    this.hideTowerTooltip();
    this.towerTooltip = this.scene.add.container(x, y - 60);
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 160, 80, 0x222244, 0.95).setOrigin(0.5);
    this.towerTooltip.add(bg);
    
    // Info
    const info = this.scene.add.text(-70, -30, 
      `Level: ${tower.getLevel()}\nDamage: ${tower.getDamage()}\nRange: ${tower.getRange()}`,
      { fontSize: '13px', color: '#fff', fontFamily: 'Arial' });
    this.towerTooltip.add(info);
    
    // Upgrade button
    const upgradeCost = tower.getLevel() * 50;
    const upgradeBtn = this.scene.add.text(-70, 20, `Upgrade (${upgradeCost})`,
      { fontSize: '13px', color: '#fff', backgroundColor: '#4CAF50', padding: { x: 8, y: 3 }, fontFamily: 'Arial' })
      .setInteractive({ useHandCursor: true });
    upgradeBtn.on('pointerdown', () => { 
      (this.scene as any).upgradeTowerFromManager(tower);
      this.hideTowerTooltip(); 
    });
    this.towerTooltip.add(upgradeBtn);
    
    // Sell button
    const sellBtn = this.scene.add.text(40, 20, `Sell`,
      { fontSize: '13px', color: '#fff', backgroundColor: '#F44336', padding: { x: 8, y: 3 }, fontFamily: 'Arial' })
      .setInteractive({ useHandCursor: true });
    sellBtn.on('pointerdown', () => { 
      (this.scene as any).sellTowerFromManager(tower);
      this.hideTowerTooltip(); 
    });
    this.towerTooltip.add(sellBtn);
    
    // Highlight selected tower
    const towerAny = tower as any;
    if (towerAny.setTint) towerAny.setTint(0xFFD700);
  }

  private hideTowerTooltip(): void {
    if (this.towerTooltip) { 
      this.towerTooltip.destroy(); 
      this.towerTooltip = null; 
    }
    if (this.selectedTower) {
      const towerAny = this.selectedTower as any;
      if (towerAny.clearTint) towerAny.clearTint();
    }
    this.selectedTower = null;
  }

  private showUpgradePanel(tower: Tower, energyCredits: number): void {
    this.hideUpgradePanel();
    this.selectedTower = tower;
    
    this.upgradePanel = this.scene.add.container(20, 250);
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 250, 120, 0x222244, 0.9);
    this.upgradePanel.add(bg);
    
    // Tower info
    const towerInfo = this.scene.add.text(10, 10, `Tower Level ${tower.getLevel()}`, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
    this.upgradePanel.add(towerInfo);
    
    const damageInfo = this.scene.add.text(10, 35, `Damage: ${tower.getDamage()}`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
    this.upgradePanel.add(damageInfo);
    
    const rangeInfo = this.scene.add.text(10, 55, `Range: ${tower.getRange()}`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
    this.upgradePanel.add(rangeInfo);
    
    // Upgrade button
    const upgradeCost = tower.getLevel() * 50;
    const upgradeBtn = this.scene.add.text(10, 80, `Upgrade (${upgradeCost})`, {
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: energyCredits >= upgradeCost ? '#4CAF50' : '#666666',
      padding: { x: 10, y: 5 },
      fontFamily: 'Arial',
    }).setInteractive({ useHandCursor: true });
    
    upgradeBtn.on('pointerdown', () => {
      (this.scene as any).upgradeTowerFromManager(tower);
    });
    
    upgradeBtn.on('pointerover', () => {
      if (energyCredits >= upgradeCost) {
        upgradeBtn.setStyle({ backgroundColor: '#388E3C' });
      }
    });
    
    upgradeBtn.on('pointerout', () => {
      if (energyCredits >= upgradeCost) {
        upgradeBtn.setStyle({ backgroundColor: '#4CAF50' });
      }
    });
    
    this.upgradePanel.add(upgradeBtn);
  }

  private hideUpgradePanel(): void {
    if (this.upgradePanel) {
      this.upgradePanel.destroy();
      this.upgradePanel = null;
    }
  }

  // Getters
  get isPlacing(): boolean { return this.isPlacingTower; }
  get placingTowerType(): string { return this.selectedTowerType; }
  get selectedTowerInstance(): Tower | null { return this.selectedTower; }
} 